import React, {useEffect, useRef, useState} from 'react';
import {Box, styled, SxProps} from '@mui/material';

const Container = styled(Box)(() => ({
  position: 'relative',
  overflow: 'hidden',
}));

const VideoElement = styled('video')<{
  isVideoLoaded: boolean;
  isHovered: boolean;
}>(({isVideoLoaded, isHovered}) => ({
  objectFit: 'cover',
  display: 'block',
  visibility: isVideoLoaded ? 'visible' : 'hidden',
  position: 'absolute',
  opacity: isHovered ? 1 : 0,
  transition: 'opacity 0.2s ease-in-out',
}));

const ThumbnailImage = styled('img')(() => ({
  objectFit: 'cover',
  display: 'block',
}));

export interface HoverableVideoProps {
  testID?: string;
  videoUrl: string;
  thumbnailUrl: string;
  width: string | number;
  height: string | number;
  isHovered: boolean;
  preloadVideo?: boolean;
  sx?: SxProps;
  muted?: boolean;
  loop?: boolean;
  onVideoLoad?: () => void;
  onVideoError?: (error: unknown) => void;
  onImageError?: (error: unknown) => void;
}

function HoverableVideo({
  testID = 'hoverable-video',
  videoUrl,
  thumbnailUrl,
  width,
  height,
  isHovered,
  preloadVideo = true,
  muted = true,
  loop = true,
  sx,
  onVideoLoad,
  onVideoError,
  onImageError,
}: HoverableVideoProps) {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
    onVideoLoad?.();
  };

  const handleVideoError = (error: unknown) => {
    // eslint-disable-next-line no-console
    console.error('HoverableVideo: Error loading video:', error);
    setIsVideoLoaded(false);
    onVideoError?.(error);
  };

  const handleImageError = (error: unknown) => {
    console.error('HoverableVideo: Error loading image:', error);
    onImageError?.(error);
  };

  useEffect(() => {
    if (!videoRef.current || !isVideoLoaded) return;
    if (isHovered) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isHovered, isVideoLoaded]);

  return (
    <Container data-testid={testID} sx={sx}>
      {isHovered || preloadVideo ? (
        <VideoElement
          ref={videoRef}
          src={videoUrl}
          poster={thumbnailUrl}
          width={width}
          height={height}
          isVideoLoaded={isVideoLoaded}
          isHovered={isHovered}
          preload="auto"
          muted={muted}
          playsInline
          loop={loop}
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        />
      ) : null}
      <ThumbnailImage
        src={thumbnailUrl}
        alt=""
        width={width}
        height={height}
        onError={handleImageError}
      />
    </Container>
  );
}

export default HoverableVideo;
